
import { useState } from 'react';
import { useFadeIn } from '../../../hooks/useFadeIn';

const faqs = [
  {
    q: 'しみることはありますか',
    a: '一時的にしみることがありますが、多くは軽度で一過性です。\n\nホワイトニングでは、施術中または施術後に一時的な知覚過敏の症状が出ることがあります。\n\n当院では施術前にお口の状態を確認し、\n・むし歯や歯周病の有無\n・もともとの知覚過敏の有無\nなどを踏まえて、しみやすさのリスクを事前にお伝えしています。\n\n不安が強い場合や、リスクが高いと判断した場合は、無理に施術を行うことはありません。',
  },
  {
    q: 'どれくらい白くなりますか',
    a: '歯の状態によって白くなり方には個人差があります。\n\nホワイトニングの効果は、\n・歯の質\n・着色の原因\n・これまでの生活習慣\nなどによって異なります。\n\n当院では、施術前の現在の歯の状態を確認し、期待できる白さの目安と限界をお伝えしています。納得いただいたうえで施術を選んでいただくことを大切にしています。',
  },
  {
    q: '効果はどれくらい続きますか',
    a: '生活習慣によって異なりますが、徐々に後戻りは起こります。\n\nホワイトニング後の白さは、飲食物や喫煙の有無、日常のケアによって持続期間が変わります。\n\n時間の経過とともに少しずつ後戻りは起こりますが、定期的なケアやホワイトニングによって白さを維持しやすくなります。\n\n当院では、施術後の過ごし方やメンテナンスについてもご説明しています。',
  },
];

interface FaqItemProps {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqItem({ faq, index, isOpen, onToggle }: FaqItemProps) {
  const item = useFadeIn({ direction: 'up', delay: index * 80 });
  return (
    <div
      ref={item.ref}
      style={item.style}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      <button
        className="w-full flex items-center gap-4 p-8 text-left cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <span className="text-base">Q</span>
        </div>
        <h3 className="text-lg lg:text-xl text-gray-900 flex-1 font-serif">{faq.q}</h3>
        <span
          className="w-6 h-6 flex items-center justify-center text-gray-400 flex-shrink-0 transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <i className="ri-arrow-down-s-line text-xl"></i>
        </span>
      </button>
      <div
        className="transition-all duration-300 ease-in-out overflow-hidden"
        style={{ maxHeight: isOpen ? '600px' : '0px', opacity: isOpen ? 1 : 0 }}
      >
        <div className="px-8 pb-8 pl-[88px]">
          <p className="text-base text-gray-900 mb-6">A. {faq.a.split('\n')[0]}</p>
          <div className="space-y-4 text-gray-700">
            {faq.a
              .split('\n')
              .slice(1)
              .map((line, i) =>
                line.startsWith('・') ? (
                  <p key={i} className="pl-4 border-l-2 border-gray-300">
                    {line}
                  </p>
                ) : line ? (
                  <p key={i}>{line}</p>
                ) : null
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WhiteningFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const heading = useFadeIn({ direction: 'up' });
  const ctaHeading = useFadeIn({ direction: 'up' });
  const ctaButtons = useFadeIn({ direction: 'up', delay: 150 });

  return (
    <section className="py-32 px-6 lg:px-12 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* CTA上部 */}
        <div ref={ctaHeading.ref} style={ctaHeading.style} className="text-center mb-20">
          <h2 className="text-3xl lg:text-4xl text-gray-900 mb-8 leading-relaxed font-serif">
            カウンセリングは無料です。お気軽に相談ください。
          </h2>
          <div ref={ctaButtons.ref} style={ctaButtons.style} className="flex flex-col items-center gap-4 mb-12 max-w-xs mx-auto">
            <a
              href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#3E5F8F] hover:bg-[#344F78] text-white text-sm px-8 py-4 rounded-full transition-colors duration-300 whitespace-nowrap cursor-pointer shadow-sm w-full"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm"></i>
              </span>
              WEB予約
            </a>
            <a
              href="tel:05018094594"
              className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-800 text-sm px-8 py-4 rounded-full transition-colors duration-300 whitespace-nowrap cursor-pointer shadow-sm border border-gray-200 w-full"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </span>
              電話で相談
            </a>
          </div>
          <div className="w-72 h-px bg-gray-300 mx-auto"></div>
        </div>

        {/* FAQ */}
        <div ref={heading.ref} style={heading.style} className="mb-12">
          <p className="text-lg text-gray-700 text-center">よくいただくご質問</p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <FaqItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
